import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ButtonDirective } from '@coreui/angular-pro';
import { TranslatePipe } from 'i18n';

import { CompanionOverview, HealthCompanionService } from '../../../services/health-companion.service';

/**
 * The "Einstellungen" tab (plan §13.3 points 2, 3, 5, 7): the AI switch, the
 * data export (JSON / CSV), the documents the companion may read, the safety
 * rules' version and the verified help contacts, the consent record, and the
 * deletion of everything.
 */
@Component({
  selector: 'app-companion-settings',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslatePipe, ButtonDirective],
  templateUrl: './companion-settings.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CompanionSettingsComponent {
  private companion  = inject(HealthCompanionService);
  private destroyRef = inject(DestroyRef);

  overview = input.required<CompanionOverview>();
  readOnly = input(false);
  lang     = input('en');
  changed  = output<void>();

  busy = signal(false);
  error = signal<string | null>(null);
  deleteConfirm = signal('');
  deleted = signal<number | null>(null);

  profile = computed(() => this.overview().profile);

  setAi(enabled: boolean): void {
    if (this.busy()) return;
    this.busy.set(true);
    this.companion.setAi(enabled).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => { this.busy.set(false); this.changed.emit(); },
      error: () => { this.busy.set(false); this.error.set('error'); }
    });
  }

  export(format: 'json' | 'csv'): void {
    this.companion.export(format).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: blob => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `companion-${new Date().toISOString().slice(0, 10)}.${format}`;
        a.click();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      },
      error: () => this.error.set('error')
    });
  }

  eraseAll(): void {
    if (this.deleteConfirm() !== 'DELETE' || this.busy()) return;
    this.busy.set(true);
    this.companion.eraseAll().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: r => { this.busy.set(false); this.deleted.set(r.deleted); this.deleteConfirm.set(''); this.changed.emit(); },
      error: () => { this.busy.set(false); this.error.set('error'); }
    });
  }
}
